import React, {useState} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import {useNavigate} from "react-router";
import {SeoHead} from "./SeoHead";
import {RichEditor} from "../admin/RichEditor";
import {useAboutContent} from "../firebase/useAboutContent";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {ProseArticle} from "./ProseArticle";

export const About = () => {
    const navigate = useNavigate();
    const [forceRefresh, setForceRefresh] = useState(0);
    const [aboutContent, updateAboutContent] = useAboutContent({forceRefresh});
    const isAdmin = useCheckIfAdmin();
    const [editMode, setEditMode] = useState(false);

    const saveHandler = data => {
        updateAboutContent(data);
        setForceRefresh(fr => fr + 1);
        setEditMode(false);
    };

    return (
        <Box sx={{height: "100%", py: {xs: 2, sm: 3}}}>
            <SeoHead
                title="Om kurset"
                description="Lær mer om metoden, bakgrunnen og teorien bak Kurs i bekymringsmestring – et integrativt og helhetlig kurs bygget på metakognitiv terapi, mindfulness og emosjonsfokuserte prinsipper."
                path="/about"
            />
            <Container maxWidth="md" disableGutters sx={{px: 0}}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/#om-kurset")}
                        sx={{
                            minHeight: 44,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "text.secondary",
                            borderRadius: 2,
                            "&:hover": {color: "primary.main"},
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: 2,
                            },
                        }}
                    >
                        Tilbake til forsiden
                    </Button>

                    {isAdmin && !editMode && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setEditMode(true)}
                            sx={{
                                minHeight: 44,
                                px: 2.5,
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                boxShadow: "none",
                                "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            Rediger
                        </Button>
                    )}
                </Box>

                {isAdmin && editMode && (
                    <Box sx={{mb: 4}}>
                        <Alert
                            severity="info"
                            sx={{
                                mb: 3,
                                borderRadius: 3,
                                border: "1px solid rgba(40, 82, 56, 0.15)",
                                bgcolor: "rgba(243, 247, 244, 0.9)",
                                color: "text.primary",
                                "& .MuiAlert-icon": {color: "primary.main"},
                            }}
                        >
                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                <strong>Redigeringsmodus:</strong> Gjør endringer i innholdet nedenfor.
                                Forhåndsvisningen oppdateres automatisk etter hvert som du skriver. Klikk
                                «Lagre» for å publisere.
                            </Typography>
                        </Alert>
                        <RichEditor
                            initialData={aboutContent}
                            onSave={saveHandler}
                            onCancel={() => setEditMode(false)}
                        />
                    </Box>
                )}

                {!editMode && (
                    <>
                        <Paper
                            elevation={0}
                            sx={{
                                p: {xs: 2.5, sm: 4, md: 6},
                                borderRadius: 4,
                                bgcolor: "#FFFFFF",
                                border: "1px solid rgba(40, 82, 56, 0.08)",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                                mb: 4,
                            }}
                        >
                            <Box sx={{mb: 3, display: "flex", flexWrap: "wrap", gap: 1}}>
                                <Chip
                                    label="Kunnskap og metode"
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(40, 82, 56, 0.08)",
                                        color: "primary.main",
                                        fontWeight: 700,
                                        fontSize: "0.75rem",
                                    }}
                                />
                                <Chip
                                    label="Metakognitiv terapi"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: "rgba(40, 82, 56, 0.2)",
                                        color: "text.secondary",
                                        fontSize: "0.75rem",
                                    }}
                                />
                                <Chip
                                    label="Mindfulnessbasert terapi"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: "rgba(40, 82, 56, 0.2)",
                                        color: "text.secondary",
                                        fontSize: "0.75rem",
                                    }}
                                />
                                <Chip
                                    label="Emosjonsfokusert terapi"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: "rgba(40, 82, 56, 0.2)",
                                        color: "text.secondary",
                                        fontSize: "0.75rem",
                                    }}
                                />
                            </Box>

                            <ProseArticle html={aboutContent} />
                        </Paper>

                        {/* Discovery CTAs after reading */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"},
                                gap: 2.5,
                                mb: 6,
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    bgcolor: "rgba(250, 248, 245, 0.8)",
                                    border: "1px solid rgba(40, 82, 56, 0.08)",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{fontWeight: 700, color: "primary.main", mb: 1, fontSize: "1.1rem"}}
                                    >
                                        Finn kursholder nær deg
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{color: "text.secondary", mb: 2.5, lineHeight: 1.6}}
                                    >
                                        Se oversikt over autoriserte kursholdere og planlagte kurs over hele landet.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => navigate("/#finn-kursholder")}
                                    sx={{
                                        minHeight: 44,
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        alignSelf: "flex-start",
                                        "&:focus-visible": {
                                            outline: "2px solid",
                                            outlineColor: "primary.main",
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    Finn kursholder
                                </Button>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    bgcolor: "rgba(243, 247, 244, 0.8)",
                                    border: "1px solid rgba(40, 82, 56, 0.08)",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{fontWeight: 700, color: "primary.main", mb: 1, fontSize: "1.1rem"}}
                                    >
                                        Prøv en meditasjonsøvelse
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{color: "text.secondary", mb: 2.5, lineHeight: 1.6}}
                                    >
                                        Lær oppmerksomt nærvær med våre tilgjengelige lydfiler og veiledninger.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => navigate("/#meditasjon")}
                                    sx={{
                                        minHeight: 44,
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        alignSelf: "flex-start",
                                        boxShadow: "none",
                                        "&:focus-visible": {
                                            outline: "2px solid",
                                            outlineColor: "primary.main",
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    Gå til øvelser
                                </Button>
                            </Paper>
                        </Box>
                    </>
                )}
            </Container>
        </Box>
    );
};